import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { Form, FormApi, useFormState } from 'informed';
import {
  AuthContext,
  CommonContext,
  dateFormat,
  playerFields,
  removeSpaces,
  updatePlayer,
  UpdatePlayerContext,
  useApi,
  User,
} from '@repo-level/core-front';
import { Trans, useTranslation } from 'react-i18next';
import { REASON_FOR_UPDATING_USER } from '@repo-level/core-front/contexts/auth/reducer';
import { parsePhoneNumber } from 'libphonenumber-js';
import { useCountryOptions } from 'hooks/common';

import { Input } from 'components/form/Input';
import {
  adultValidator,
  composeValidators,
  dateRegexValidator,
  required,
} from 'helpers/validators';
import { InputDate } from 'components/form/Date';
import {
  InputPhoneNumber,
  ScAddressInput,
  ScInputNameSurname,
  validatePhone,
} from 'components/form/InputPhoneNumber';
import { FormSection } from 'components/form/groups/FormSection';
import { Button } from 'components/buttons';
import { countriesWithPhoneNumber } from 'helpers/countriesWithPhoneNumber';
import { Spinner } from 'components/Spinner';
import { showTooltip } from 'helpers/tooltip';
import { TooltipTypes } from 'modules/globalTooltip';
import { ModalsContext, ModalType } from 'contexts/modal';
import { translationFactory } from 'helpers/trans';
import { AnalyticsEventName } from 'api/analytics';
import { useAnalytics } from 'helpers/analytics';
import { ModalTemplateSettings } from 'components/ModalTemplate/types';
import { ModalTemplate } from 'components/ModalTemplate';
import { ModalDefaultProps } from 'components/Modal';
import { notEmpty } from 'helpers/common';
import { maskPhone } from 'helpers/masks';
import { useFranceLocale } from 'hooks/franceLocale';
import { Select } from 'components/form/Select';
import { StagContext } from 'contexts/stag';
import { LocalStorageKeys } from 'helpers/localStorage';

import { ScAdditionalDataFormContainer } from './styled';
import { useMedia } from 'modules/common/hooks/useMedia';

const validateDate = composeValidators(
  required,
  dateRegexValidator,
  adultValidator
);

export interface FullInformationFormApi {
  firstName: string;
  lastName: string;
  dateOfBirth: {
    day: string;
    month: string;
    year: string;
  };
  mobilePhone: {
    code: string;
    number: string;
  };
  country: string;
  city: string;
  address: string;
  postalCode: string;
  state?: string;
}

export const FullInformation = forwardRef<HTMLDivElement, ModalDefaultProps>(
  ({ onClose }, ref) => {
    const { t } = useTranslation();
    const { sendEventToAnalytics } = useAnalytics();

    const { isFR, isFRWithStag, isFRWithoutStag } = useFranceLocale();

    const { currentIp } = useContext(CommonContext);
    const { fetchUser, user, pendingUser, pendingWallets } = useContext(
      AuthContext
    );
    const { isMobileS } = useMedia();
    const { isStagNDB } = useContext(StagContext);
    const hasConfirmEmailShown = localStorage.getItem(
      LocalStorageKeys.ConfirmEmailNDB
    );

    const formApiRef = useRef<FormApi<FullInformationFormApi>>();

    const [
      requestUpdatePlayer,
      { pending: pendingUpdatePlayer, error: updatePlayerErrors },
    ] = useApi(updatePlayer);

    const [
      requestStatePlayer,
      { data: dataStatePlayer, pending: pendingStatePlayer },
    ] = useApi(playerFields);

    useEffect(() => {
      if (!dataStatePlayer && currentIp?.countryCode) {
        requestStatePlayer(user?.country || currentIp.countryCode);
      }
    }, [currentIp?.countryCode, dataStatePlayer, requestStatePlayer]);

    const templateSettings: ModalTemplateSettings = useMemo(
      () =>
        isMobileS
          ? {
              type: 'mobile',
              mode: {
                type: 'default',
              },
            }
          : {
              type: 'desktop',
              size: 'large',
              mode: {
                type: 'default',
              },
            },
      [isMobileS]
    );

    const errors = useMemo(() => {
      if (!updatePlayerErrors?.errors) return null;
      return Object.keys(updatePlayerErrors.errors).map((key) => ({
        field: key,
        message: Object.values(updatePlayerErrors.errors[key]),
      }));
    }, [updatePlayerErrors?.errors]);

    useEffect(() => {
      if (!errors?.length) return;
      errors.forEach(({ field, message }) => {
        if (field === 'mobilePhone') {
          formApiRef.current?.setError('mobilePhone.number', message);
        } else {
          formApiRef.current?.setError(field, message);
        }
      });
    }, [errors]);

    const { handleModal } = useContext(ModalsContext);

    const defaultCountry = useMemo(() => {
      return currentIp?.countryCode;
    }, [currentIp]);

    const countryOptions = useCountryOptions();

    const statesOptions = useMemo(() => {
      if (!dataStatePlayer) return null;

      const { fields } = dataStatePlayer;

      const stateField = fields.find((item) => item.field === 'state');

      return stateField?.inclusion.in.map((state: string) => ({
        name: state,
        value: state,
      }));
    }, [dataStatePlayer]);

    const showErrorTooltip = () => {
      showTooltip({
        type: TooltipTypes.ERROR,
        translate: true,
        title: translationFactory(
          'common:auth.fullInformation.tooltips.failure.title'
        ),
        text: translationFactory(
          'common:auth.fullInformation.tooltips.failure.text'
        ),
      });
    };

    const handleSubmit = async ({ ...values }: FullInformationFormApi) => {
      const mobilePhone = (() => {
        if (values?.mobilePhone?.code == null) return;

        const mobileCode = countriesWithPhoneNumber.find(
          ({ iso2 }) => iso2 === values.mobilePhone.code
        );

        if (mobileCode == null) return;

        return removeSpaces(
          `+${mobileCode.dialCode}${values.mobilePhone.number}`
        );
      })();
      const mobilePhoneWithoutDialCode = removeSpaces(
        values.mobilePhone.number
      );

      const { error } = await requestUpdatePlayer({
        context: UpdatePlayerContext.Edition,
        player: {
          ...values,
          dateOfBirth: dateFormat(
            `${values.dateOfBirth.day}/${values.dateOfBirth.month}/${values.dateOfBirth.year}`
          ),
          ...(mobilePhone && !isFR ? { mobilePhone } : {}),
          ...(mobilePhoneWithoutDialCode && isFR
            ? { mobilePhoneWithoutDialCode }
            : {}),
        } as Partial<User>,
      }).request;

      if (error != null) {
        sendEventToAnalytics({
          name: AnalyticsEventName.MODAL__FULL_INFORMATION__FORM__ERROR,
          attributes: { additionalData: JSON.stringify(error) },
        });
        showErrorTooltip();
        return;
      }

      showTooltip({
        type: TooltipTypes.SUCCESS,
        translate: true,
        title: translationFactory(
          'common:auth.fullInformation.tooltips.success.title'
        ),
        text: translationFactory(
          'common:auth.fullInformation.tooltips.success.text'
        ),
      });

      await fetchUser(REASON_FOR_UPDATING_USER.REGISTRATION);

      sendEventToAnalytics({
        name: AnalyticsEventName.MODAL__FULL_INFORMATION__FORM__SUCCESS,
      });

      onClose && onClose();

      handleModal(ModalType.Deposit)();
    };

    const completed = isFRWithoutStag
      ? !!user?.firstName && !!user?.lastName && !!user?.dateOfBirth
      : !!user?.firstName &&
        !!user?.lastName &&
        !!user?.dateOfBirth &&
        !!user?.mobilePhone &&
        !!user?.country &&
        !!user?.state &&
        !!user?.city &&
        !!user?.address &&
        !!user?.postalCode;

    const submitButtonParams = useMemo(
      () => (isMobileS ? { wide: true } : { _width: 232 }),
      [isMobileS]
    );

    const handleClickSubmit = () => {
      sendEventToAnalytics({
        name: AnalyticsEventName.MODAL__FULL_INFORMATION__FORM__SUBMIT,
      });

      if (!formApiRef.current) return;
      const { errors } = formApiRef.current.getState();
      if (Object.keys(errors).length) {
        sendEventToAnalytics({
          name: AnalyticsEventName.MODAL__FULL_INFORMATION__FORM__ERROR,
          attributes: { additionalData: JSON.stringify(errors) },
        });
        showErrorTooltip();
      }
    };

    const handleClose = useCallback(() => {
      onClose && onClose();

      if (isStagNDB && !hasConfirmEmailShown) {
        handleModal(ModalType.ConfirmEmailForNDBModal)();
      }
    }, [handleModal, onClose, isStagNDB]);

    return pendingUser || pendingWallets ? (
      <Spinner size={64} absoluteCenter />
    ) : (
      <ModalTemplate
        ref={ref}
        onClose={handleClose}
        settings={templateSettings}
      >
        <Form<FullInformationFormApi>
          getApi={(api) => {
            formApiRef.current = api;
          }}
          onSubmit={handleSubmit}
        >
          <Analytics />

          <ScAdditionalDataFormContainer>
            <div className="header">
              <div className="title">{t('common:messageFromElf')}</div>
              <div className="slogan">{t('common:messageFromElfDescr')}</div>
            </div>

            <div className="fields-container">
              <FormSection title={t('glossary:name')}>
                <ScInputNameSurname>
                  <Input
                    initialValue={user?.firstName || undefined}
                    disabled={notEmpty(user?.firstName)}
                    animatedPlaceholder={t('glossary:firstName')}
                    validateOnBlur
                    field="firstName"
                    validate={required}
                  />
                  <Input
                    initialValue={user?.lastName || undefined}
                    disabled={notEmpty(user?.lastName)}
                    animatedPlaceholder={t('glossary:lastName')}
                    validateOnBlur
                    field="lastName"
                    validate={required}
                  />
                </ScInputNameSurname>
              </FormSection>
              <FormSection title={t('glossary:dateOfBirth')}>
                <InputDate
                  initialValue={user?.dateOfBirth || undefined}
                  disabled={notEmpty(user?.dateOfBirth)}
                  field="dateOfBirth"
                  validate={validateDate}
                />
              </FormSection>
              {isFRWithStag ? (
                <>
                  <FormSection title={t('glossary:phoneNumber')}>
                    <Input
                      initialValue={
                        user?.mobilePhone
                          ? (parsePhoneNumber(user?.mobilePhone)
                              .nationalNumber as string)
                          : undefined
                      }
                      disabled={notEmpty(user?.mobilePhone)}
                      field="mobilePhone.number"
                      animatedPlaceholder="Number"
                      mask={(v) => maskPhone(v?.toString(), 'FR')}
                      validate={(v) => validatePhone(v, 'FR')}
                      validateOnBlur
                    />
                  </FormSection>
                  <FormSection title={t('glossary:address')}>
                    <ScAddressInput>
                      <Input
                        initialValue={user?.city || undefined}
                        disabled={notEmpty(user?.city)}
                        validateOnBlur
                        field="city"
                        animatedPlaceholder={t('glossary:city')}
                        validate={required}
                      />
                      <Input
                        initialValue={user?.address || undefined}
                        disabled={notEmpty(user?.address)}
                        validateOnBlur
                        field="address"
                        animatedPlaceholder={t('glossary:address')}
                        validate={required}
                      />
                      <Input
                        initialValue={user?.postalCode || undefined}
                        disabled={notEmpty(user?.postalCode)}
                        validateOnBlur
                        field="postalCode"
                        animatedPlaceholder={t('glossary:postal')}
                        validate={required}
                      />
                    </ScAddressInput>
                  </FormSection>
                </>
              ) : (
                <>
                  <FormSection title={t('glossary:phoneNumber')}>
                    <InputPhoneNumber
                      initialValue={user?.mobilePhone || undefined}
                      initialCodeValue={defaultCountry}
                      disabled={notEmpty(user?.city)}
                      field="mobilePhone"
                      withValidate
                      validateOnBlur
                    />
                  </FormSection>
                  <FormSection
                    title={t('glossary:address')}
                    noMargin={notEmpty(user?.country)}
                  >
                    {notEmpty(user?.country) && (
                      <p className="country-info-msg">
                        <Trans
                          ns="common"
                          i18nKey="addressInfoMsg"
                          components={{
                            l: <span id="intercomcustomLauncher" />,
                          }}
                        />
                      </p>
                    )}
                    <ScAddressInput>
                      <Select
                        initialValue={
                          user?.country || defaultCountry || undefined
                        }
                        disabled={notEmpty(user?.country)}
                        field="country"
                        animatedPlaceholder={t('glossary:country')}
                        validate={required}
                        options={countryOptions}
                        onSelect={(country: any) => {
                          requestStatePlayer(country);
                        }}
                        search
                        withBorderMode
                        selectedOptionWithoutIcon
                        minOptionHeight={48}
                      />
                      {pendingStatePlayer ? (
                        <Spinner size={48} center />
                      ) : (
                        statesOptions && (
                          <Select
                            disabled={notEmpty(user?.state)}
                            field="state"
                            animatedPlaceholder={t('glossary:state')}
                            validate={required}
                            options={statesOptions}
                            search
                            withBorderMode
                            selectedOptionWithoutIcon
                            minOptionHeight={48}
                            validateOnBlur
                          />
                        )
                      )}
                      <Input
                        initialValue={user?.city || undefined}
                        disabled={notEmpty(user?.city)}
                        validateOnBlur
                        field="city"
                        animatedPlaceholder={t('glossary:city')}
                        validate={required}
                      />
                      <Input
                        initialValue={user?.address || undefined}
                        disabled={notEmpty(user?.address)}
                        validateOnBlur
                        field="address"
                        animatedPlaceholder={t('glossary:address')}
                        validate={required}
                      />
                      <Input
                        initialValue={user?.postalCode || undefined}
                        disabled={notEmpty(user?.postalCode)}
                        validateOnBlur
                        field="postalCode"
                        animatedPlaceholder={t('glossary:postal')}
                        validate={required}
                      />
                    </ScAddressInput>
                  </FormSection>
                </>
              )}
            </div>
            <div className="submit-btn-container">
              <Button
                type="submit"
                green
                xLarge
                pending={pendingUpdatePlayer}
                disabled={completed}
                onClick={handleClickSubmit}
                {...submitButtonParams}
              >
                {t('glossary:confirm')}
              </Button>
            </div>
          </ScAdditionalDataFormContainer>
        </Form>
      </ModalTemplate>
    );
  }
);

const Analytics = () => {
  const { touched } = useFormState();
  const touchedForm = Object.values(touched).some((t) => t);
  const { sendEventToAnalytics } = useAnalytics();

  useEffect(() => {
    sendEventToAnalytics({
      name: AnalyticsEventName.MODAL__FULL_INFORMATION__FORM__APPEARANCE,
    });
  }, [sendEventToAnalytics]);

  useEffect(() => {
    if (touchedForm) {
      sendEventToAnalytics({
        name: AnalyticsEventName.MODAL__FULL_INFORMATION__FORM__TOUCHED,
      });
    }
  }, [touchedForm, sendEventToAnalytics]);

  return null;
};
